import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {ContainerSummary} from 'constants/SampleConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {requestFetchManagedModel} from 'models/ManagedModelUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import * as StageRunActions from 'workflows/StageRunActions';
import {isContainable} from 'workflows/util/StageRunSampleUtil';
import {
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

import OptionalContainerLinkWithLocation from './OptionalContainerLinkWithLocation';
import SelectContainerFromContainableCell from './SelectContainerFromContainableCell';

interface ConnectedProps {
  bioentitySummary: FileConstants.BioentitySummary | null;
  isLoading: boolean;
}

function mapStateToProps(
  state: BenchlingState,
  {
    inputSampleId,
    stageRunSampleById,
  }: {inputSampleId: string; stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}}
): ConnectedProps {
  const sample = stageRunSampleById[inputSampleId];
  const bioentityId = propsToBioentityId({inputSampleId, stageRunSampleById});
  const bioentitySummary = bioentityId ? state.models.fileSummariesById[bioentityId] : null;
  return {
    bioentitySummary,
    isLoading: Boolean(!sample || (bioentityId && !bioentitySummary)),
  };
}

function propsToBioentityId({
  inputSampleId,
  stageRunSampleById,
}: {
  inputSampleId: string;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
}): string | null {
  const sample = stageRunSampleById[inputSampleId];
  return sample && sample.fileId ? sample.fileId : null;
}

export interface InputTableContainerCellProps extends ConnectedProps {
  stageRun: StageRun;
  inputSampleId: string;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  locked: boolean;
  viewer?: boolean;
}

interface State {
  isContainerSaving: boolean;
  optimisticContainerId: string | null;
}

/**
 * Cell in the Input Table that renders and allows to select a container.
 */
class InputTableContainerCell extends React.Component<InputTableContainerCellProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
    inputSampleId: PropTypes.string.isRequired,
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
    locked: PropTypes.bool.isRequired,
    viewer: PropTypes.bool,

    // mapStateToProps
    bioentitySummary: FileConstants.FILE_SUMMARY_SHAPE, // Needed for isContainable
    isLoading: PropTypes.bool.isRequired,
  };

  state: State = {
    isContainerSaving: false,
    optimisticContainerId: null,
  };

  componentDidMount(): void {
    const bioentityId = propsToBioentityId(this.props);
    if (bioentityId) {
      requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, bioentityId);
    }
  }

  componentDidUpdate(): void {
    const bioentityId = propsToBioentityId(this.props);
    if (bioentityId) {
      requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, bioentityId);
    }
  }

  _handleSelectContainer = async (container: ContainerSummary): Promise<void> => {
    const {inputSampleId, stageRun, stageRunSampleById} = this.props;

    const sample = stageRunSampleById[inputSampleId];

    const containerId = container ? container.id : null;
    if (sample.containerId === containerId) {
      return;
    }

    this.setState({isContainerSaving: true, optimisticContainerId: containerId});

    try {
      await StageRunActions.replaceInputSample(stageRun.id, {
        sampleIdToRemove: inputSampleId,
        batchIdToAdd: sample.batch ? sample.batch.id : null,
        bioentityIdToAdd: sample.fileId,
        containerIdToAdd: containerId,
      });
    } finally {
      this.setState({isContainerSaving: false, optimisticContainerId: null});
    }
  };

  render(): JSX.Element {
    const sample = this.props.stageRunSampleById[this.props.inputSampleId];
    if (this.props.isLoading) {
      return (
        <RichObjectTable.Cell primary={true}>
          <span className="u-faded u-italic">Loading</span>
        </RichObjectTable.Cell>
      );
    }
    if (isContainable(sample, this.props.bioentitySummary) && !this.props.locked && !this.props.viewer) {
      return (
        <SelectContainerFromContainableCell
          sample={sample}
          isSaving={this.state.isContainerSaving}
          optimisticContainerId={this.state.optimisticContainerId}
          onSelectContainer={this._handleSelectContainer}
        />
      );
    }
    return (
      <RichObjectTable.Cell primary={true}>
        <OptionalContainerLinkWithLocation
          containerId={sample.containerId}
          truncateLocation={!this.props.viewer}
        />
      </RichObjectTable.Cell>
    );
  }
}

export default connect(mapStateToProps)(InputTableContainerCell);
